import { inject } from 'mobx-react';
import * as React from 'react';
import { DateField, FormInputsProvider, LodgingFormEvent, SubmitButton, TravelersField, TravelersFieldTriggerType, useFormAction } from '@shared-ui/lodging-form';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { PropertyAvailability } from '@shared-ui/retail-lodging-property-availability';
import { useBexApi } from '@shared-ui/bex-api-context';
import { useLocalization } from '@shared-ui/localization-context';
import { useRefContext } from '@shared-ui/ref-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import { getDateRangeInput, getRooms } from 'stores/adapters';
import { SearchCriteriaModel } from 'stores/models';
import { ActionProviderDatedMobile } from './action-provider';
import { SharedUiTravelersSelectorWrapper } from '../offers/shared-ui-traveler-selector-wrapper';
export const START_DATE_REF_NAME = 'startDate';
export const END_DATE_REF_NAME = 'endDate';
/**
 * Translate a Date object into a DateInput
 * @protected
 */
export const parseDate = date => ({
  day: date.getDate(),
  month: date.getMonth() + 1,
  year: date.getFullYear()
});
export const SharedUiDateFieldWrapper = inject('context', 'experiment', 'currentHotel')(({
  experiment,
  context,
  currentHotel,
  buttonText,
  onDismiss,
  closeOnSubmit = true,
  invalidDatesText
}) => {
  var _a, _b;
  const {
    registerTarget
  } = useRefContext();
  const hotelId = currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.hotelId;
  const searchCriteria = currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.searchCriteria;
  const dateRangeInput = searchCriteria && getDateRangeInput(searchCriteria);
  // Register targets so that datepicker can be opened from different parts of the page
  const startDateRef = registerTarget(START_DATE_REF_NAME);
  const endDateRef = registerTarget(END_DATE_REF_NAME);
  const dispatch = useFormAction();
  const [placeholder, setPlaceholder] = React.useState(context === null || context === void 0 ? void 0 : context.localDateFormat);
  const {
    formatText
  } = useLocalization();
  React.useEffect(() => {
    setPlaceholder(formatText('hotels.search.form.toDate.placeholder'));
  }, []);
  const dateFieldData = React.useMemo(() => {
    var _a, _b, _c;
    const isVrbo = ((_b = (_a = experiment === null || experiment === void 0 ? void 0 : experiment.exposures) === null || _a === void 0 ? void 0 : _a.Vrbex_generic) === null || _b === void 0 ? void 0 : _b.bucket) === 1;
    const output = {
      startId: 'hotels-check-in',
      startLabel: formatText('hotels.search.form.fromDateHyphen.label'),
      startName: 'startDate',
      startPlaceholder: placeholder || '',
      startInvalid: '',
      endId: 'hotels-check-out',
      endLabel: formatText('hotels.search.form.toDateHyphen.label'),
      endName: 'endDate',
      endPlaceholder: placeholder || '',
      endInvalid: '',
      buttonText,
      daysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
      midnightBookingOffset: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
      __typename: 'LodgingFormDateField',
      validations: [{
        __typename: 'StartDateRequiredValidation',
        error: invalidDatesText || formatText('hotels.search.validation.invalidFromDate.text'),
        events: [LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT]
      }, {
        __typename: 'EndDateRequiredValidation',
        error: invalidDatesText || formatText('hotels.search.validation.invalidToDate.text'),
        events: [LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT]
      }, {
        __typename: 'DateOrderValidation',
        error: formatText('hotels.search.validation.invalidDateRange.text'),
        events: [LodgingFormEvent.ON_LOAD, LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT]
      }]
    };
    if (!isVrbo) {
      (_c = output.validations) === null || _c === void 0 ? void 0 : _c.push({
        __typename: 'MaxDateRangeValidation',
        error: formatText('hotels.search.validation.invalidDateDateRangeLength.text'),
        maxDateRange: 28,
        events: [LodgingFormEvent.ON_LOAD, LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT]
      });
    }
    return output;
  }, [buttonText, (_b = (_a = experiment === null || experiment === void 0 ? void 0 : experiment.exposures) === null || _a === void 0 ? void 0 : _a.Vrbex_generic) === null || _b === void 0 ? void 0 : _b.bucket, formatText, invalidDatesText, placeholder]);
  const onSubmitCB = (startDate, endDate) => {
    dispatch({
      type: 'change',
      field: 'dates',
      value: {
        dates: {
          checkInDate: startDate ? parseDate(startDate) : null,
          checkOutDate: endDate ? parseDate(endDate) : null
        }
      }
    });
  };
  const dateRange = dateRangeInput ? {
    end: dateRangeInput.checkOutDate,
    start: dateRangeInput.checkInDate
  } : null;
  return React.createElement(Experiment, {
    name: "Vrbex_Availability_Date_Selector"
  }, React.createElement(ExperimentControl, null, React.createElement(Viewport, null, React.createElement(ViewSmall, null, React.createElement(DateField, {
    fieldName: "dates",
    data: dateFieldData,
    closeOnSubmit: closeOnSubmit,
    onDismiss: onDismiss,
    isOpenOverride: true,
    startDateRef: startDateRef,
    endDateRef: endDateRef
  })), React.createElement(ViewLarge, null, React.createElement(DateField, {
    fieldName: "dates",
    data: dateFieldData,
    closeOnSubmit: closeOnSubmit,
    startDateRef: startDateRef,
    endDateRef: endDateRef
  })))), React.createElement(ExperimentVariant, {
    bucket: 1
  }, React.createElement(Viewport, null, React.createElement(ViewSmall, null, React.createElement(PropertyAvailability, {
    startDateRef: startDateRef,
    endDateRef: endDateRef,
    inputs: {
      eid: String(hotelId),
      dateRange
    },
    onSubmit: onSubmitCB,
    skipSsr: true
  })), React.createElement(ViewLarge, null, React.createElement(PropertyAvailability, {
    startDateRef: startDateRef,
    endDateRef: endDateRef,
    inputs: {
      eid: String(hotelId),
      dateRange
    },
    onSubmit: onSubmitCB,
    skipSsr: true
  })))));
});
export const SharedUiTravelersWrapper = inject('context', 'currentHotel')(({
  doneLabel,
  closeOnSubmit = true,
  trigger = TravelersFieldTriggerType.INPUT,
  isSingleUnit = false
}) => {
  const {
    formatText
  } = useLocalization();
  const travelersData = {
    __typename: 'LodgingFormTravelers',
    label: formatText('hotels.search.form.travelers.label'),
    travelersSummary: {
      __typename: 'TravelersSummary',
      roomsAndGuests: isSingleUnit ? 'hotels.roomPicker.roomSummary.singleUnit' : 'hotels.roomPicker.roomSummary'
    },
    roomPicker: {
      __typename: 'RoomPicker',
      toolBarIconLabel: formatText('hotels.roomPicker.cancelButton.a11yLabel'),
      toolBarTitle: formatText('hotels.roomPicker.title'),
      addRoomText: isSingleUnit ? '' : formatText('hotels.roomPicker.addRoomButton.label'),
      doneLabel: doneLabel || formatText('hotels.roomPicker.doneButton.label'),
      roomAddedLiveAnnounce: formatText('hotels.roomPicker.roomAdded.liveAnnounce', '{0}'),
      room: {
        __typename: 'RoomField',
        roomTitleAriaLabel: isSingleUnit ? '' : formatText('hotels.roomPicker.room.a11yLabelRoom', '{0}', '{1}'),
        roomTitle: isSingleUnit ? '' : formatText('hotels.roomPicker.room.title', '{0}'),
        adultsStepInput: {
          __typename: 'StepInput',
          title: formatText('hotels.roomPicker.adults.text'),
          increaseText: formatText('hotels.roomPicker.increaseAdults.text', '{0}'),
          decreaseText: formatText('hotels.roomPicker.decreaseAdults.text', '{0}'),
          caption: null
        },
        childrenStepInput: {
          __typename: 'StepInput',
          title: formatText('hotels.roomPicker.children.text'),
          increaseText: formatText('hotels.roomPicker.increaseChildren.text', '{0}'),
          decreaseText: formatText('hotels.roomPicker.decreaseChildren.text', '{0}'),
          caption: formatText('hotels.roomPicker.children.caption')
        },
        removeRoomButtonLabel: formatText('hotels.roomPicker.removeRoomButton.label'),
        removeRoomButtonAriaLabel: formatText('hotels.roomPicker.removeRoomButton.a11yLabel', '{0}', '{1}'),
        roomRemovedLiveAnnounce: formatText('hotels.roomPicker.roomRemoved.liveAnnounce', '{0}'),
        childrenAges: {
          __typename: 'ChildrenAges',
          selectChildAgeLabel: formatText('hotels.roomPicker.childAgeInput.label', '{0}'),
          youngestChildAgeText: formatText('hotels.roomPicker.youngestChildAge.text'),
          childAgeUpdatedLiveAnnounce: formatText('hotels.roomPicker.childUpdated.liveAnnounce', '{0}', '{1}')
        }
      }
    }
  };
  return React.createElement(TravelersField, {
    fieldName: "travelers",
    data: travelersData,
    closeOnSubmit: closeOnSubmit,
    trigger: trigger
  });
});
export const SharedUiSubmitButtonWrapper = ({
  text
}) => {
  const submitButtonData = {
    __typename: 'LodgingFormSubmitButton',
    label: text
  };
  return React.createElement(SubmitButton, {
    fieldName: "submit",
    data: submitButtonData
  });
};
export const SharedUiTravelersLinkWrapper = inject('currentHotel')(({
  currentHotel,
  handleChange
}) => {
  const {
    formatText
  } = useLocalization();
  const hotelId = currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.hotelId;
  const searchCriteria = currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.searchCriteria;
  const {
    context
  } = useBexApi();
  const inputs = React.useMemo(() => ({
    propertyId: String(hotelId),
    dateRange: searchCriteria ? getDateRangeInput(searchCriteria) : null,
    roomInput: searchCriteria ? getRooms(searchCriteria) : []
  }), [hotelId, searchCriteria === null || searchCriteria === void 0 ? void 0 : searchCriteria.startDate, searchCriteria === null || searchCriteria === void 0 ? void 0 : searchCriteria.endDate, searchCriteria === null || searchCriteria === void 0 ? void 0 : searchCriteria.rooms]);
  const variable = Object.assign(Object.assign({}, inputs || {}), {
    context
  });
  const doneLabel = formatText('hotels.infosite.searchForm.availabilityButton.label');
  return React.createElement(ActionProviderDatedMobile, {
    onChange: handleChange
  }, React.createElement(FormInputsProvider, {
    value: variable
  }, React.createElement(Experiment, {
    name: "Vrbex_traveler_selector"
  }, React.createElement(ExperimentControl, null, React.createElement(SharedUiTravelersWrapper, {
    doneLabel: doneLabel,
    trigger: TravelersFieldTriggerType.LINK,
    closeOnSubmit: false
  })), React.createElement(ExperimentVariant, {
    bucket: 1
  }, React.createElement(SharedUiTravelersSelectorWrapper, null)))));
});
SharedUiTravelersLinkWrapper.displayName = 'SharedUiTravelersLinkWrapper';